// Weekly Catch: distinct species a trainer catches with a seal this UTC week
// (Monday 00:00 UTC, the weekly quests' reset). Only hunt catches count, the
// auto-sold ones too; buying, ascending, hatching and admin-thrown seals do not.
//
// Milestones pay once a week, on the catch that reaches them: coins (kills'
// worth at the trainer's best hunt) and seals of that hunt's tier. No diamonds:
// free diamonds stay low. When a week ends, the board's top places get a title
// to keep (shared/pass.ts holds kept titles and badges); #1 also a chat badge.
// Admin and dev-flagged accounts are left off the board, so never placed.
import { weekOf } from './recipes.ts';
import { bestHunt, sealFor } from './quests.ts';
import { killCoins } from './xp.ts';

export interface WeekCatchStep { at: number; coinKills: number; seals: number }

export const WEEK_CATCH = {
  steps: [
    { at: 5, coinKills: 50, seals: 10 },
    { at: 10, coinKills: 100, seals: 20 },
    { at: 25, coinKills: 200, seals: 30 },
    { at: 50, coinKills: 300, seals: 40 },
    { at: 100, coinKills: 500, seals: 60 },
  ] as WeekCatchStep[],
  /** Kept titles for last week's board: #1, and #2 to podiumTo. */
  champion: 'Catch Champion',
  podium: 'Top Catcher',
  podiumTo: 10,
  badge: { id: 'week-catch', name: 'Catch Crown', glyph: '✪' },
} as const;

/** A finished week, kept until the next week's podium has been given. */
export interface WeekCatchPrev { week: string; n: number; at: number }

export interface WeekCatchState {
  /** The week's Monday (YYYY-MM-DD). */
  week: string;
  species: string[];
  /** When the latest new species was caught: the earlier wins a tie. */
  at: number;
  /** Milestones paid this week (count of WEEK_CATCH.steps). */
  paid: number;
  prev?: WeekCatchPrev;
}

type Holder = { weekCatch?: WeekCatchState };

/** This week's state, rolled over (last week kept as prev) when the week changed. */
export function weekCatchNow(t: Holder, now: number): WeekCatchState {
  const week = weekOf(now);
  const w = t.weekCatch;
  if (w?.week === week) return w;
  const prev = w && w.species.length ? { week: w.week, n: w.species.length, at: w.at } : w?.prev;
  return t.weekCatch = { week, species: [], at: 0, paid: 0, ...(prev ? { prev } : {}) };
}

/** Distinct species this week (0 when the stored week is over). */
export const weekCatchCount = (t: Holder, now: number) => (t.weekCatch?.week === weekOf(now) ? t.weekCatch.species.length : 0);

/** A trainer's count and tie for a given week, from this week's state or the kept one. */
export function weekCatchFor(t: Holder, week: string): { n: number; at: number } | null {
  const w = t.weekCatch;
  if (!w) return null;
  if (w.week === week) return w.species.length ? { n: w.species.length, at: w.at } : null;
  return w.prev?.week === week ? { n: w.prev.n, at: w.prev.at } : null;
}

/** What a step pays at this trainer level. */
export function stepReward(step: WeekCatchStep, trainerLevel: number): { coins: number; seal: string; seals: number } {
  const h = bestHunt(trainerLevel).level;
  return { coins: killCoins(h) * step.coinKills, seal: sealFor(h), seals: step.seals };
}

/** A seal catch of `species`: counts it once a week, and returns the steps it reached (each paid once). */
export function weekCatchHit(t: Holder, species: string, now: number): WeekCatchStep[] {
  const w = weekCatchNow(t, now);
  if (w.species.includes(species)) return [];
  w.species.push(species);
  w.at = now;
  const out: WeekCatchStep[] = [];
  while (w.paid < WEEK_CATCH.steps.length && w.species.length >= WEEK_CATCH.steps[w.paid].at) out.push(WEEK_CATCH.steps[w.paid++]);
  return out;
}

/** The kept title a finished week's place gives, or null. */
export const podiumTitle = (rank: number): string | null => (rank === 1 ? WEEK_CATCH.champion : rank <= WEEK_CATCH.podiumTo ? WEEK_CATCH.podium : null);

/** The Monday before `week`. */
export const weekBefore = (week: string) => new Date(Date.parse(`${week}T00:00:00Z`) - 7 * 86_400_000).toISOString().slice(0, 10);
